import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CDataTable,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CPagination,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getItem, getList, postItem } from "../api/generics";
import { SUCCESS } from "../utils/constants/tags";
import AnimatedCheckbox from "../components/checkbox/AnimatedCheckbox";
import PermissionRequired from "../components/permissions/PermissionRequired";

const PAGE_SIZE = 10;

interface AiConnection {
  id: number;
  userEmail: string | null;
  tokenPrefix: string;
  name: string | null;
  origin: string;
  createdAt: string;
  lastUsedAt: string | null;
}

interface AiCall {
  id: number;
  userId: number | null;
  userEmail: string | null;
  connectionName: string | null;
  tokenPrefix: string | null;
  endpoint: string;
  queryRange: string | null;
  rowsReturned: number | null;
  statusCode: number;
  createdAt: string;
}

interface AiEnablementChange {
  id: number;
  aiEnabled: boolean;
  userEmail: string | null;
  createdAt: string;
}

interface AiSettings {
  aiEnabled: boolean | null;
}

const formatDate = (value: string | null) =>
  value === null ? "—" : new Date(value).toLocaleString("es-PY");

/**
 * One list of the screen, asking the server for one page at a time.
 *
 * Paged by the server and not in the browser: the history and the calls only
 * ever grow, and a list that loaded a fixed number of the latest rows would
 * silently stop showing the oldest ones.
 *
 * `reloadKey` brings it back to the first page, where whatever just changed
 * shows up.
 */
const Paginated = <T,>({
  url,
  reloadKey,
  onCount,
  columns,
  scopedSlots,
  emptyMessage,
}: {
  url: string;
  reloadKey: number;
  onCount?: (count: number) => void;
  columns: { key: string; label: string }[];
  scopedSlots?: Record<string, (item: T) => JSX.Element>;
  emptyMessage: string;
}) => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<T[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [url, reloadKey]);

  useEffect(() => {
    // A page answered late must not overwrite the one asked for after it.
    let current = true;

    const load = async () => {
      setLoading(true);

      // getList parses the body as JSON without looking at it first, so an
      // answer that is not JSON — a route the server does not have — throws
      // instead of coming back as a failure.
      const response = await getList<T>(
        url,
        PAGE_SIZE,
        (page - 1) * PAGE_SIZE,
      ).catch(() => null);

      if (!current) {
        return;
      }

      if (
        response !== null &&
        response.status === SUCCESS &&
        response.data !== undefined
      ) {
        setItems(response.data.items);
        setCount(response.data.count);
        setFailed(false);
        onCount?.(response.data.count);
      } else {
        setFailed(true);
      }

      setLoading(false);
    };

    load();

    return () => {
      current = false;
    };
    // onCount is left out on purpose: a new callback is not a new page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, page, reloadKey]);

  const pages = Math.max(Math.ceil(count / PAGE_SIZE), 1);

  if (failed) {
    return <p className="text-danger mb-0">No se pudo cargar la lista.</p>;
  }

  if (!loading && count === 0) {
    return <p className="text-muted mb-0">{emptyMessage}</p>;
  }

  return (
    <>
      <CDataTable
        items={items}
        fields={columns}
        scopedSlots={scopedSlots}
        loading={loading}
        hover
        striped
        size="sm"
      />
      {pages > 1 && (
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} de{" "}
            {count}
          </small>
          <CPagination
            activePage={page}
            pages={pages}
            onActivePageChange={setPage}
            align="end"
            size="sm"
          />
        </div>
      )}
    </>
  );
};

const AiSettingsPage = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );
  // Resolved by the server, which is the only one that knows a demo's user may
  // open this screen: the permissions the panel receives have the demo
  // blocklist applied. The server refuses the endpoints on its own as well.
  const parametersLoaded = useSelector(
    (state: RootState) => state.taxPayerParameters.loaded,
  );
  const manageable = useSelector(
    (state: RootState) => state.taxPayerParameters.data.aiSettingsManageable,
  );
  // The sidebar hides this screen when SmartDoc has not sold the channel. The
  // route has no permission of its own, so the page has to ask the same.
  const aiAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.aiAccess,
  );

  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [connectionCount, setConnectionCount] = useState(0);
  const [callCount, setCallCount] = useState(0);

  // Bumped after a change to the switch, so the history shows it on its first
  // page. The other two lists do not move with the switch.
  const [historyKey, setHistoryKey] = useState(0);

  /**
   * Forces the checkbox to remount.
   *
   * AnimatedCheckbox keeps its own state and flips it on click, and only
   * re-syncs when the `value` prop changes. Cancelling the confirmation leaves
   * `value` exactly as it was, so the box would stay flipped and show the
   * opposite of what the company actually has. Remounting rebuilds its state
   * from the real value.
   */
  const [checkboxKey, setCheckboxKey] = useState(0);

  const dismiss = () => {
    setPendingValue(null);
    setCheckboxKey((current) => current + 1);
  };

  const load = useCallback(async () => {
    if (taxPayer.id === undefined) {
      return;
    }

    setLoading(true);
    const response = await getItem<AiSettings>(
      `/tax_payers/${taxPayer.id}/ai_settings/`,
    );

    if (response.status === SUCCESS && response.data !== undefined) {
      setSettings(response.data);
      setError(null);
    } else {
      setError(response.detail ?? "No se pudo cargar la configuración.");
    }

    setLoading(false);
  }, [taxPayer.id]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async () => {
    if (pendingValue === null) {
      return;
    }

    setSaving(true);
    const response = await postItem<{ aiEnabled: boolean }>(
      `/tax_payers/${taxPayer.id}/ai_settings/`,
      { aiEnabled: pendingValue },
    );

    if (response.status === SUCCESS) {
      await load();
      setHistoryKey((current) => current + 1);
    } else {
      setError(response.detail ?? "No se pudo guardar el cambio.");
    }

    // Also on the way out of a successful save: the box has to end up showing
    // what came back, not what was clicked.
    dismiss();
    setSaving(false);
  };

  if (parametersLoaded && (aiAccess !== true || !manageable)) {
    return <PermissionRequired />;
  }

  if (loading) {
    return (
      <CContainer fluid>
        <CCard>
          <CCardBody>Cargando…</CCardBody>
        </CCard>
      </CContainer>
    );
  }

  if (settings === null) {
    return (
      <CContainer fluid>
        <CCard>
          <CCardBody>
            {error ?? "No se pudo cargar la configuración."}
          </CCardBody>
        </CCard>
      </CContainer>
    );
  }

  const enabled = settings.aiEnabled === true;

  return (
    <CContainer fluid>
      {error !== null && (
        <CCard>
          <CCardBody className="text-danger">{error}</CCardBody>
        </CCard>
      )}

      <CCard>
        <CCardBody>
          <h2 style={{ marginLeft: "15px" }}>Inteligencia Artificial</h2>
        </CCardBody>
      </CCard>

      {/* The channel covers part of SmartDoc, not all of it. Said up front so a
          request it cannot handle reads as a limit, not as a failure, without
          scaring anybody out of trying. */}
      <CAlert color="info">
        <strong>Función en desarrollo.</strong> Las personas de esta empresa
        pueden conectar el asistente de IA que ya usan —Claude, ChatGPT, Gemini
        u otros— a SmartDoc y preguntarle sobre su facturación:{" "}
        <em>cuánto facturé este mes comparado con el anterior</em>,{" "}
        <em>qué producto vendí más</em>, <em>quién es mi mejor cliente</em>, y
        todo lo que se les ocurra. También puede preparar y emitir facturas.
        Todavía no cubre todo lo que ofrece SmartDoc: lo que aún no esté
        disponible por ese medio se sigue haciendo desde el panel, como siempre.
      </CAlert>

      {/* The switch reads like "turn AI on for everyone", and it is not: it is
          the company's master switch, and each person still needs their own
          permission. Said before the switch, not after somebody flips it. */}
      <CCard>
        <CCardHeader>
          <strong>Cómo funciona</strong>
        </CCardHeader>
        <CCardBody>
          <ol className="pl-4">
            <li>
              <strong>Activar la IA acá abajo vale para toda la empresa:</strong>{" "}
              decide si en la empresa se puede usar. Activarla no le da acceso
              a nadie; desactivarla se lo corta a todos.
            </li>
            <li>
              <strong>Cada persona necesita su permiso.</strong> En{" "}
              <strong>Usuarios</strong>, al editar los permisos de cada usuario,
              buscá el grupo «Inteligencia Artificial»:
              <ul className="pl-3 mb-0">
                <li>
                  <strong>«Conectar una IA»</strong>: marcalo para que pueda
                  conectar su asistente y consultar. Sin este permiso no puede
                  usar la IA.
                </li>
                <li>
                  <strong>«Preparar borradores»</strong>: marcalo sólo si también
                  querés que prepare facturas y las confirme. Si lo dejás sin
                  marcar, su IA únicamente consulta.
                </li>
              </ul>
              Si más adelante querés quitarle el acceso a esa persona, desmarcá
              esos permisos en <strong>Usuarios</strong>.
            </li>
            <li>
              Con eso, esa persona ve <strong>Conexiones de IA</strong> en el
              menú de su usuario, en la barra superior, con los pasos para
              conectar su asistente.
            </li>
          </ol>
          <p className="text-muted mb-0">
            La IA sólo puede hacer lo que esa persona ya puede hacer en el
            panel: lo que no le permitas a ella, tampoco se lo permite a su IA.
          </p>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>Estado en esta empresa</strong>
        </CCardHeader>
        <CCardBody>
          <CRow className="align-items-center">
            <CCol xs="auto">
              <AnimatedCheckbox
                key={checkboxKey}
                value={enabled}
                disabled={saving}
                onChange={(next: boolean) => setPendingValue(next)}
              ></AnimatedCheckbox>
            </CCol>
            <CCol>
              <strong>{enabled ? "Activada" : "Desactivada"}</strong>
              <div className="text-muted">
                {enabled
                  ? "Pueden usarla los usuarios con el permiso «Conectar una IA»."
                  : "Nadie puede consultar los datos de esta empresa con una IA."}
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>Quién la activó o desactivó</strong>
        </CCardHeader>
        <CCardBody>
          <Paginated<AiEnablementChange>
            url={`/tax_payers/${taxPayer.id}/ai_settings/history/`}
            reloadKey={historyKey}
            emptyMessage="Nadie activó ni desactivó todavía la IA en esta empresa."
            columns={[
              { key: "createdAt", label: "Fecha" },
              { key: "aiEnabled", label: "Cambio" },
              { key: "userEmail", label: "Usuario" },
            ]}
            scopedSlots={{
              createdAt: (item: AiEnablementChange) => (
                <td>{formatDate(item.createdAt)}</td>
              ),
              aiEnabled: (item: AiEnablementChange) => (
                <td>{item.aiEnabled ? "Activó" : "Desactivó"}</td>
              ),
              userEmail: (item: AiEnablementChange) => (
                <td>{item.userEmail ?? "—"}</td>
              ),
            }}
          />
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>Quién la tiene conectada</strong>{" "}
          <small className="text-muted">
            {connectionCount}{" "}
            {connectionCount === 1 ? "conexión" : "conexiones"}
          </small>
        </CCardHeader>
        <CCardBody>
          <p className="text-muted">
            Para quitarle el acceso a alguien, sacale el permiso «Conectar una
            IA» en la pantalla de usuarios.
          </p>
          <Paginated<AiConnection>
            url={`/tax_payers/${taxPayer.id}/ai_settings/connections/`}
            reloadKey={0}
            onCount={setConnectionCount}
            emptyMessage="Todavía no hay ninguna conexión."
            columns={[
              { key: "userEmail", label: "Usuario" },
              { key: "name", label: "Conexión" },
              { key: "createdAt", label: "Conectada" },
              { key: "lastUsedAt", label: "Último uso" },
            ]}
            scopedSlots={{
              userEmail: (item: AiConnection) => (
                <td>{item.userEmail ?? "—"}</td>
              ),
              name: (item: AiConnection) => (
                <td>
                  {item.name ?? "Sin nombre"}{" "}
                  <code className="text-muted">{item.tokenPrefix}…</code>
                </td>
              ),
              createdAt: (item: AiConnection) => (
                <td>{formatDate(item.createdAt)}</td>
              ),
              lastUsedAt: (item: AiConnection) => (
                <td>{formatDate(item.lastUsedAt)}</td>
              ),
            }}
          />
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>Qué se consultó</strong>{" "}
          <small className="text-muted">
            {callCount} {callCount === 1 ? "consulta" : "consultas"}
          </small>
        </CCardHeader>
        <CCardBody>
          <p className="text-muted">
            Qué se consultó y cuándo. No incluye el contenido de la respuesta.
          </p>
          <Paginated<AiCall>
            url={`/tax_payers/${taxPayer.id}/ai_settings/calls/`}
            reloadKey={0}
            onCount={setCallCount}
            emptyMessage="Todavía no hay consultas registradas."
            columns={[
              { key: "createdAt", label: "Fecha" },
              { key: "userEmail", label: "Usuario" },
              { key: "connection", label: "Conexión" },
              { key: "endpoint", label: "Consulta" },
              { key: "queryRange", label: "Período" },
              { key: "rowsReturned", label: "Filas" },
              { key: "statusCode", label: "Resultado" },
            ]}
            scopedSlots={{
              createdAt: (item: AiCall) => <td>{formatDate(item.createdAt)}</td>,
              userEmail: (item: AiCall) => <td>{item.userEmail ?? "—"}</td>,
              connection: (item: AiCall) => (
                <td>
                  {item.tokenPrefix === null ? (
                    "—"
                  ) : (
                    <>
                      {item.connectionName ?? "Sin nombre"}{" "}
                      <code className="text-muted">{item.tokenPrefix}…</code>
                    </>
                  )}
                </td>
              ),
              endpoint: (item: AiCall) => (
                <td>
                  <code>{item.endpoint}</code>
                </td>
              ),
              queryRange: (item: AiCall) => <td>{item.queryRange ?? "—"}</td>,
              rowsReturned: (item: AiCall) => (
                <td>{item.rowsReturned ?? "—"}</td>
              ),
              statusCode: (item: AiCall) => <td>{item.statusCode}</td>,
            }}
          />
        </CCardBody>
      </CCard>

      {/* Turning the channel off cuts every person in the company at once, so it
          is not something a stray click should do. */}
      <CModal
        show={pendingValue !== null}
        onClose={dismiss}
        centered
      >
        <CModalHeader closeButton>
          <strong>
            {pendingValue === true
              ? "Activar la Inteligencia Artificial"
              : "Desactivar la Inteligencia Artificial"}
          </strong>
        </CModalHeader>
        <CModalBody>
          {pendingValue === true ? (
            <p className="mb-0">
              Los usuarios que tengan el permiso «Conectar una IA» van a poder
              conectar su asistente y consultar la facturación, cada uno con los
              permisos que ya tiene en el panel.
            </p>
          ) : (
            <p className="mb-0">
              Esta empresa deja de estar disponible para consultas con IA, de
              inmediato y para todos sus usuarios.
            </p>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            disabled={saving}
            onClick={dismiss}
          >
            Cancelar
          </CButton>
          <CButton
            color={pendingValue === true ? "primary" : "danger"}
            disabled={saving}
            onClick={confirm}
          >
            {saving ? "Guardando…" : "Confirmar"}
          </CButton>
        </CModalFooter>
      </CModal>
    </CContainer>
  );
};

export default AiSettingsPage;
